<style lang="scss">
@import "resources/sass/v1/abstracts/_variables";

.demo-request-form {
    .form-wrapper {
        position: relative;
        padding-bottom: 80px;
        padding-top: 80px;

        @media screen and (min-width: $screen-md) {
            padding-bottom: 120px;
        }

        @media screen and (min-width: $screen-lg) {
            padding: 80px 206px 160px 206px;
        }

        .form {
            box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.03);
            padding: 40px;
            background-color: $white;
            position: relative;
            z-index: 2;

            @media screen and (min-width: $screen-md) {
                padding: 80px 120px;

                &:after {
                    position: absolute;
                    content: url("/svg/shapes/demo-request-loop.svg");
                    bottom: 104px;
                    left: calc(50% + 48px);
                }
            }
        }

        &:before {
            position: absolute;
            content: url("/svg/shapes/bubble-secondary.svg");
            top: 26px;
            left: 74px;
        }
    }
}
</style>

<template>
    <div class="demo-request-form" :class="{ processing: processing }">
        <div class="container">
            <div class="text-center">
                <h2 class="mb-24">{{ title }}</h2>
                <p class="text-cut mb-0">{{ description }}</p>
            </div>

            <div class="form-wrapper">
                <div
                    class="form"
                    id="demo-request-form"
                    ref="form"
                    :class="{ processing: processing }"
                >
                    <h3 class="h4 mb-80 text-center">{{ formTitle }}</h3>
                    <ValidationObserver
                        v-slot="obs"
                        ref="observer"
                        tag="form"
                        @submit.prevent="submit()"
                    >
                        <div class="form-row">
                            <div class="col-12 col-md-6">
                                <ValidationProvider
                                    ref="first_name"
                                    vid="first_name"
                                    rules="required|min:2"
                                    v-slot="props"
                                    :name="translations.first_name"
                                    :slim="true"
                                >
                                    <div
                                        class="form-group position-relative"
                                        :class="props.classes"
                                    >
                                        <label for="first_name">{{
                                            translations.first_name
                                        }}</label>
                                        <input
                                            type="text"
                                            id="first_name"
                                            class="form-control"
                                            :placeholder="
                                                translations.first_name
                                            "
                                            v-model="formData.first_name"
                                        />
                                        <span class="error">{{
                                            props.errors[0]
                                        }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                            <div class="col-12 col-md-6">
                                <ValidationProvider
                                    ref="name"
                                    vid="name"
                                    rules="required|min:2"
                                    v-slot="props"
                                    :name="translations.name"
                                    :slim="true"
                                >
                                    <div
                                        class="form-group position-relative"
                                        :class="props.classes"
                                    >
                                        <label for="name">{{
                                            translations.name
                                        }}</label>
                                        <input
                                            type="text"
                                            id="name"
                                            class="form-control"
                                            :placeholder="translations.name"
                                            v-model="formData.name"
                                        />
                                        <span class="error">{{
                                            props.errors[0]
                                        }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="col-12 col-md-6">
                                <ValidationProvider
                                    ref="email"
                                    vid="email"
                                    rules="required|email"
                                    v-slot="props"
                                    :name="translations.email"
                                    :slim="true"
                                >
                                    <div
                                        class="form-group position-relative"
                                        :class="props.classes"
                                    >
                                        <label for="email">{{
                                            translations.email
                                        }}</label>
                                        <input
                                            type="email"
                                            id="email"
                                            class="form-control"
                                            :placeholder="translations.email"
                                            v-model="formData.email"
                                        />
                                        <span class="error">{{
                                            props.errors[0]
                                        }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                            <div class="col-12 col-md-6">
                                <ValidationProvider
                                    ref="phone"
                                    vid="phone"
                                    :rules="{
                                        required: true,
                                        regex: /((?:\+|00)[17](?: |\-)?|(?:\+|00)[1-9]\d{0,2}(?: |\-)?|(?:\+|00)1\-\d{3}(?: |\-)?)?(0\d|\([0-9]{3}\)|[1-9]{0,3})(?:((?: |\-)[0-9]{2}){4}|((?:[0-9]{2}){4})|((?: |\-)[0-9]{3}(?: |\-)[0-9]{4})|([0-9]{7}))/
                                    }"
                                    v-slot="props"
                                    :name="translations.phone"
                                    :slim="true"
                                >
                                    <div
                                        class="form-group position-relative"
                                        :class="props.classes"
                                    >
                                        <label for="phone">{{
                                            translations.phone
                                        }}</label>
                                        <input
                                            type="text"
                                            id="phone"
                                            class="form-control"
                                            :placeholder="translations.phone"
                                            v-model="formData.phone"
                                        />
                                        <span class="error">{{
                                            props.errors[0]
                                        }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="col-12">
                                <button
                                    @click.stop="submit('/custom')"
                                    :disabled="obs.invalid"
                                    class="btn btn-lg btn-secondary btn-special"
                                >
                                    <span>{{ translations.submit }}</span>
                                </button>
                            </div>
                        </div>
                    </ValidationObserver>

                    <div class="spinner-background">
                        <div class="spinner"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import formBase from "@mixins/formBase";

export default {
    mixins: [formBase],

    props: {
        title: {
            type: String,
            required: true
        },
        description: {
            type: String,
            required: true
        },
        formTitle: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            formData: {
                first_name: "",
                name: "",
                email: "",
                phone: ""
            }
        };
    }
};
</script>
